import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ClipboardList, Plus, Search, Check, X, DollarSign, Clock, 
  ChevronRight, FileText, User, AlertCircle, CheckCircle2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  proposed: { color: 'bg-blue-100 text-blue-700', icon: FileText, label: 'Proposed' },
  accepted: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Accepted' },
  in_progress: { color: 'bg-purple-100 text-purple-700', icon: Clock, label: 'In Progress' },
  completed: { color: 'bg-slate-100 text-slate-700', icon: Check, label: 'Completed' },
  declined: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Declined' }
};

const PRIORITY_LABELS = {
  1: { label: 'Urgent', color: 'text-red-600 bg-red-50' },
  2: { label: 'High', color: 'text-orange-600 bg-orange-50' },
  3: { label: 'Medium', color: 'text-yellow-600 bg-yellow-50' },
  4: { label: 'Low', color: 'text-green-600 bg-green-50' },
  5: { label: 'Elective', color: 'text-slate-600 bg-slate-50' }
};

const TOOTH_NUMBERS = Array.from({ length: 32 }, (_, i) => i + 1);
const SURFACES = ['M', 'O', 'D', 'B', 'L'];

const TreatmentPlanning = () => {
  const { token, user, isOwner } = useAuth();
  const [plans, setPlans] = useState([]);
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);

  // Form state for new/edit plan
  const [formData, setFormData] = useState({
    patient_id: '',
    title: '',
    services: [],
    total_fee: 0,
    notes: '',
    status: 'proposed'
  });

  // Service line item being added
  const [newService, setNewService] = useState({
    service_id: '',
    tooth_number: '',
    surface: '',
    priority: 3,
    notes: '',
    fee: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, patientsRes, servicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/dental/treatment-plans`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/patients`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/dental/services`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setPlans(plansRes.data);
      setPatients(patientsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      toast.error('Failed to load treatment plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.services.length === 0) {
      toast.error('Please add at least one service to the treatment plan');
      return;
    }

    try {
      if (selectedPlan) {
        await axios.put(
          `${API_URL}/api/dental/treatment-plans/${selectedPlan.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Treatment plan updated');
      } else {
        await axios.post(`${API_URL}/api/dental/treatment-plans`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Treatment plan created');
      }
      setShowModal(false);
      setSelectedPlan(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save treatment plan');
    }
  };

  const updatePlanStatus = async (planId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/dental/treatment-plans/${planId}?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Plan ${status.replace('_', ' ')}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update plan status');
    }
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      title: '',
      services: [],
      total_fee: 0,
      notes: '',
      status: 'proposed'
    });
    setNewService({
      service_id: '',
      tooth_number: '',
      surface: '',
      priority: 3,
      notes: '',
      fee: 0
    });
  };

  const openCreateModal = (patientId = '') => {
    setSelectedPlan(null);
    resetForm();
    if (patientId) {
      setFormData(prev => ({ ...prev, patient_id: patientId }));
    }
    setShowModal(true);
  };

  const openEditModal = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      patient_id: plan.patient_id,
      title: plan.title,
      services: plan.services || [],
      total_fee: plan.total_fee,
      notes: plan.notes || '',
      status: plan.status
    });
    setShowModal(true);
  };

  const addServiceToPlan = () => {
    if (!newService.service_id) {
      toast.error('Please select a service');
      return;
    }

    const service = services.find(s => s.id === newService.service_id);
    const serviceItem = {
      ...newService,
      service_name: service?.service_name,
      fee: newService.fee || service?.default_price_jmd || 0
    };

    const updatedServices = [...formData.services, serviceItem];
    const totalFee = updatedServices.reduce((sum, s) => sum + (s.fee || 0), 0);

    setFormData(prev => ({
      ...prev,
      services: updatedServices,
      total_fee: totalFee
    }));

    setNewService({
      service_id: '',
      tooth_number: '',
      surface: '',
      priority: 3,
      notes: '',
      fee: 0
    });
  };

  const removeServiceFromPlan = (index) => {
    const updatedServices = formData.services.filter((_, i) => i !== index);
    const totalFee = updatedServices.reduce((sum, s) => sum + (s.fee || 0), 0);
    setFormData(prev => ({
      ...prev,
      services: updatedServices,
      total_fee: totalFee
    }));
  };

  const getPatientName = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    return patient ? `${patient.last_name}, ${patient.first_name}` : 'Unknown';
  };

  const filteredPlans = plans.filter(plan => {
    const patientName = getPatientName(plan.patient_id).toLowerCase();
    const matchesSearch = patientName.includes(searchTerm.toLowerCase()) ||
                          plan.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || plan.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="treatment-planning">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Treatment Planning</h1>
            <p className="text-slate-500">Create and manage patient treatment plans</p>
          </div>
          <Button onClick={() => openCreateModal()} className="bg-teal-600 hover:bg-teal-700" data-testid="new-plan-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Treatment Plan
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search by patient or plan title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Plans List */}
        {filteredPlans.length === 0 ? (
          <div className="card p-8 text-center">
            <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No treatment plans found</p>
            <Button className="mt-4" onClick={() => openCreateModal()}>
              Create First Plan
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPlans.map(plan => {
              const StatusIcon = STATUS_CONFIG[plan.status]?.icon || FileText;
              const isExpanded = expandedPlan === plan.id;
              
              return (
                <div key={plan.id} className="card overflow-hidden">
                  {/* Plan Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex gap-4 items-start">
                        <div className={`p-2 rounded-lg ${STATUS_CONFIG[plan.status]?.color}`}>
                          <StatusIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{plan.title}</h3>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <User className="w-3 h-3" />
                            {getPatientName(plan.patient_id)}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="flex items-center gap-1 text-teal-600 font-medium">
                              <DollarSign className="w-4 h-4" />
                              J${plan.total_fee?.toLocaleString() || 0}
                            </span>
                            <span className="text-slate-400">
                              {plan.services?.length || 0} procedures
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[plan.status]?.color}`}>
                          {STATUS_CONFIG[plan.status]?.label}
                        </span>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t bg-slate-50 p-4">
                      {/* Services Table */}
                      <div className="bg-white rounded-lg border mb-4">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left p-3 font-medium">Procedure</th>
                              <th className="text-left p-3 font-medium">Tooth</th>
                              <th className="text-left p-3 font-medium">Surface</th>
                              <th className="text-left p-3 font-medium">Priority</th>
                              <th className="text-right p-3 font-medium">Fee</th>
                            </tr>
                          </thead>
                          <tbody>
                            {plan.services?.map((svc, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="p-3">{svc.service_name}</td>
                                <td className="p-3">{svc.tooth_number || '-'}</td>
                                <td className="p-3">{svc.surface || '-'}</td>
                                <td className="p-3">
                                  <span className={`px-2 py-0.5 rounded text-xs ${PRIORITY_LABELS[svc.priority]?.color || ''}`}>
                                    {PRIORITY_LABELS[svc.priority]?.label || 'Normal'}
                                  </span>
                                </td>
                                <td className="p-3 text-right font-medium">J${svc.fee?.toLocaleString() || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-50 font-semibold">
                            <tr>
                              <td colSpan={4} className="p-3 text-right">Total:</td>
                              <td className="p-3 text-right text-teal-600">J${plan.total_fee?.toLocaleString() || 0}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {plan.notes && (
                        <p className="text-sm text-slate-600 mb-4 italic">{plan.notes}</p>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 justify-end">
                        {plan.status === 'proposed' && (
                          <>
                            <Button size="sm" variant="outline" className="text-green-600" onClick={() => updatePlanStatus(plan.id, 'accepted')}>
                              <Check className="w-4 h-4 mr-1" /> Accept
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => updatePlanStatus(plan.id, 'declined')}>
                              <X className="w-4 h-4 mr-1" /> Decline
                            </Button>
                          </>
                        )}
                        {plan.status === 'accepted' && (
                          <Button size="sm" onClick={() => updatePlanStatus(plan.id, 'in_progress')}>
                            Start Treatment
                          </Button>
                        )}
                        {plan.status === 'in_progress' && (
                          <Button size="sm" onClick={() => updatePlanStatus(plan.id, 'completed')}>
                            Mark Completed
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openEditModal(plan)}>
                          Edit Plan
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedPlan ? 'Edit Treatment Plan' : 'Create Treatment Plan'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Patient *</Label>
                  <Select 
                    value={formData.patient_id} 
                    onValueChange={(v) => setFormData({...formData, patient_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.last_name}, {p.first_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Plan Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., Full Mouth Restoration"
                    required
                  />
                </div>
              </div>

              {/* Add Service Section */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <Label className="text-base font-semibold mb-3 block">Add Procedures</Label>
                <div className="grid grid-cols-6 gap-2 mb-2">
                  <div className="col-span-2">
                    <Select 
                      value={newService.service_id} 
                      onValueChange={(v) => {
                        const svc = services.find(s => s.id === v);
                        setNewService({
                          ...newService, 
                          service_id: v,
                          fee: svc?.default_price_jmd || 0
                        });
                      }}
                    >
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.service_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Select 
                      value={newService.tooth_number?.toString() || ''} 
                      onValueChange={(v) => setNewService({...newService, tooth_number: parseInt(v) || ''})}
                    >
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Tooth" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">N/A</SelectItem>
                        {TOOTH_NUMBERS.map(n => (
                          <SelectItem key={n} value={n.toString()}>#{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Select 
                      value={newService.surface} 
                      onValueChange={(v) => setNewService({...newService, surface: v})}
                    >
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Surface" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">N/A</SelectItem>
                        {SURFACES.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                        <SelectItem value="MOD">MOD</SelectItem>
                        <SelectItem value="MO">MO</SelectItem>
                        <SelectItem value="DO">DO</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Input
                      type="number"
                      value={newService.fee}
                      onChange={(e) => setNewService({...newService, fee: parseFloat(e.target.value) || 0})}
                      placeholder="Fee"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Button type="button" onClick={addServiceToPlan} className="w-full">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  <div className="col-span-2">
                    <Select 
                      value={newService.priority?.toString()} 
                      onValueChange={(v) => setNewService({...newService, priority: parseInt(v)})}
                    >
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Priority" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PRIORITY_LABELS).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    <Input
                      value={newService.notes}
                      onChange={(e) => setNewService({...newService, notes: e.target.value})}
                      placeholder="Procedure notes (optional)"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Added Services List */}
              {formData.services.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-2">Procedure</th>
                        <th className="text-left p-2">Tooth</th>
                        <th className="text-left p-2">Surface</th>
                        <th className="text-left p-2">Priority</th>
                        <th className="text-right p-2">Fee</th>
                        <th className="p-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.services.map((svc, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2">{svc.service_name}</td>
                          <td className="p-2">{svc.tooth_number || '-'}</td>
                          <td className="p-2">{svc.surface || '-'}</td>
                          <td className="p-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${PRIORITY_LABELS[svc.priority]?.color || ''}`}>
                              {PRIORITY_LABELS[svc.priority]?.label || 'Normal'}
                            </span>
                          </td>
                          <td className="p-2 text-right">J${svc.fee?.toLocaleString()}</td>
                          <td className="p-2">
                            <Button 
                              type="button" 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 h-6 w-6 p-0"
                              onClick={() => removeServiceFromPlan(idx)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-teal-50">
                      <tr className="font-semibold">
                        <td colSpan={4} className="p-2 text-right">Total:</td>
                        <td className="p-2 text-right text-teal-700">J${formData.total_fee.toLocaleString()}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional plan notes..."
                  rows={2}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setSelectedPlan(null); }} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700" disabled={formData.services.length === 0}>
                  {selectedPlan ? 'Update Plan' : 'Create Plan'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TreatmentPlanning;
